import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  AppBar,
  Toolbar,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CardActionArea,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  MenuItem,
  Select
} from '@mui/material';
import {
  Laptop as LaptopIcon,
  Build as BuildIcon,
  Assignment as AssignmentIcon,
  History as HistoryIcon,
  QrCodeScanner as QrIcon,
  Logout as LogoutIcon,
  People as PeopleIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Block as BlockIcon,
  Close as CloseIcon,
  NavigateBefore,
  NavigateNext,
  Assessment as ReportIcon,
  Security as SecurityIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { laptopAPI, repairAPI } from '../services/api';

const Dashboard = () => {
  const navigate = useNavigate();
  const { admin, logout } = useAuth();
  const [stats, setStats] = useState({
    total: 0,
    assigned: 0,
    readyToUse: 0,
    inRepair: 0,
    notReadyToUse: 0,
    noUse: 0
  });
  const [repairStats, setRepairStats] = useState({
    totalCost: 0,
    activeRepairs: 0
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedStatusValue, setSelectedStatusValue] = useState(null);
  const [filteredLaptops, setFilteredLaptops] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalLaptops, setTotalLaptops] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Repair records state
  const [repairRecords, setRepairRecords] = useState([]);
  const [repairPage, setRepairPage] = useState(1);
  const [repairPageSize, setRepairPageSize] = useState(10);
  const [totalRepairs, setTotalRepairs] = useState(0);
  const [totalRepairPages, setTotalRepairPages] = useState(0);
  const [loadingRepairs, setLoadingRepairs] = useState(false);

  useEffect(() => {
    loadStats();
    loadRepairRecords();
  }, []);

  const loadStats = async () => {
    try {
      const [laptopResponse, repairResponse] = await Promise.all([
        laptopAPI.getStats(),
        repairAPI.getStats()
      ]);

      if (laptopResponse.data.success) {
        const { total, byStatus } = laptopResponse.data.data;
        const statusMap = {};
        byStatus.forEach(item => {
          statusMap[item._id] = item.count;
        });

        setStats({
          total,
          assigned: statusMap.assigned || 0,
          readyToUse: statusMap['ready-to-use'] || 0,
          inRepair: statusMap['in-repair'] || 0,
          notReadyToUse: statusMap['not-ready-to-use'] || 0,
          noUse: statusMap['no-use'] || 0
        });
      }

      if (repairResponse.data.success) {
        const { byStatus, costs } = repairResponse.data.data;
        const activeRepairs = byStatus
          .filter(s => ['pending', 'in-progress'].includes(s._id))
          .reduce((sum, s) => sum + s.count, 0);

        setRepairStats({
          totalCost: costs.totalCost || 0,
          activeRepairs
        });
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const loadRepairRecords = async (currentPage = repairPage, currentPageSize = repairPageSize) => {
    setLoadingRepairs(true);
    try {
      const response = await repairAPI.getAll({
        page: currentPage,
        limit: currentPageSize,
        sortBy: 'sentDate',
        sortOrder: 'desc'
      });

      if (response.data.success) {
        setRepairRecords(response.data.data);
        setTotalRepairs(response.data.total);
        setTotalRepairPages(response.data.pages);
      }
    } catch (error) {
      console.error('Error loading repair records:', error);
      setRepairRecords([]);
    } finally {
      setLoadingRepairs(false);
    }
  };

  const handleRepairPageChange = (newPage) => {
    setRepairPage(newPage);
    loadRepairRecords(newPage, repairPageSize);
  };

  const handleRepairPageSizeChange = (event) => {
    const newSize = parseInt(event.target.value);
    setRepairPageSize(newSize);
    setRepairPage(1);
    loadRepairRecords(1, newSize);
  };

  const getRepairStatusColor = (status) => {
    const colors = {
      completed: 'success',
      'in-progress': 'info',
      pending: 'warning',
      cancelled: 'default'
    };
    return colors[status] || 'default';
  };

  const getRepairStatusLabel = (status) => {
    const labels = {
      completed: 'Completed',
      'in-progress': 'In Progress',
      pending: 'Pending',
      cancelled: 'Cancelled'
    };
    return labels[status] || status;
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString();
  };

  const loadLaptopsForDialog = async (status, currentPage, currentPageSize) => {
    setLoading(true);
    try {
      let response;
      const params = { page: currentPage, limit: currentPageSize };

      if (status === 'all') {
        response = await laptopAPI.getAll(params);
      } else {
        response = await laptopAPI.getAll({ ...params, status });
      }

      if (response.data.success) {
        setFilteredLaptops(response.data.data);
        setTotalLaptops(response.data.total);
        setTotalPages(response.data.pages);
      }
    } catch (error) {
      console.error('Error loading laptops:', error);
      setFilteredLaptops([]);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusClick = async (status, label) => {
    setSelectedStatus(label);
    setSelectedStatusValue(status);
    setDialogOpen(true);
    setPage(1);
    setPageSize(50);
    await loadLaptopsForDialog(status, 1, 50);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedStatus(null);
    setSelectedStatusValue(null);
    setFilteredLaptops([]);
    setPage(1);
    setPageSize(50);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    loadLaptopsForDialog(selectedStatusValue, newPage, pageSize);
  };

  const handlePageSizeChange = (event) => {
    const newSize = parseInt(event.target.value);
    setPageSize(newSize);
    setPage(1);
    loadLaptopsForDialog(selectedStatusValue, 1, newSize);
  };

  const handleLaptopClick = (laptopId) => {
    handleCloseDialog();
    navigate(`/laptops/${laptopId}`);
  };

  const getStatusColor = (status) => {
    const colors = {
      assigned: 'success',
      'ready-to-use': 'info',
      'in-repair': 'error',
      'not-ready-to-use': 'warning',
      'no-use': 'default',
      retired: 'default'
    };
    return colors[status] || 'default';
  };

  const getStatusLabel = (status) => {
    const labels = {
      assigned: 'Assigned',
      'ready-to-use': 'Ready to Use',
      'in-repair': 'In Repair',
      'not-ready-to-use': 'Not Ready to Use',
      'no-use': 'No Use',
      retired: 'Retired'
    };
    return labels[status] || status;
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar position="static">
        <Toolbar>
          <Box
            component="img"
            src="https://sapphirus.com/wp-content/uploads/2020/09/Logo_sapphirus.png"
            alt="Sapphirus Logo"
            sx={{
              height: '40px',
              width: 'auto',
              mr: 2
            }}
          />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Laptop Inventory Management
          </Typography>
          <Typography variant="body2" sx={{ mr: 2 }}>
            {admin?.name}
          </Typography>
          <IconButton color="inherit" onClick={handleLogout}>
            <LogoutIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Grid container spacing={3}>
          {/* Statistics Cards - Now Clickable */}
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <Card sx={{ cursor: 'pointer', transition: 'transform 0.2s', '&:hover': { transform: 'scale(1.05)' } }}>
              <CardActionArea onClick={() => handleStatusClick('all', 'Total Laptops')}>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom variant="body2">
                    Total Laptops
                  </Typography>
                  <Typography variant="h4">{stats.total}</Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={4} lg={2}>
            <Card sx={{ bgcolor: 'success.light', cursor: 'pointer', transition: 'transform 0.2s', '&:hover': { transform: 'scale(1.05)' } }}>
              <CardActionArea onClick={() => handleStatusClick('assigned', 'Assigned')}>
                <CardContent>
                  <Typography color="white" gutterBottom variant="body2">
                    Assigned
                  </Typography>
                  <Typography variant="h4" color="white">
                    {stats.assigned}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={4} lg={2}>
            <Card sx={{ bgcolor: 'info.light', cursor: 'pointer', transition: 'transform 0.2s', '&:hover': { transform: 'scale(1.05)' } }}>
              <CardActionArea onClick={() => handleStatusClick('ready-to-use', 'Ready to Use')}>
                <CardContent>
                  <Typography color="white" gutterBottom variant="body2">
                    Ready to Use
                  </Typography>
                  <Typography variant="h4" color="white">
                    {stats.readyToUse}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={4} lg={2}>
            <Card sx={{ bgcolor: 'error.light', cursor: 'pointer', transition: 'transform 0.2s', '&:hover': { transform: 'scale(1.05)' } }}>
              <CardActionArea onClick={() => handleStatusClick('in-repair', 'In Repair')}>
                <CardContent>
                  <Typography color="white" gutterBottom variant="body2">
                    In Repair
                  </Typography>
                  <Typography variant="h4" color="white">
                    {stats.inRepair}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={4} lg={2}>
            <Card sx={{ bgcolor: 'warning.light', cursor: 'pointer', transition: 'transform 0.2s', '&:hover': { transform: 'scale(1.05)' } }}>
              <CardActionArea onClick={() => handleStatusClick('not-ready-to-use', 'Not Ready to Use')}>
                <CardContent>
                  <Typography color="white" gutterBottom variant="body2">
                    Not Ready to Use
                  </Typography>
                  <Typography variant="h4" color="white">
                    {stats.notReadyToUse}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={4} lg={2}>
            <Card sx={{ bgcolor: 'grey.400', cursor: 'pointer', transition: 'transform 0.2s', '&:hover': { transform: 'scale(1.05)' } }}>
              <CardActionArea onClick={() => handleStatusClick('no-use', 'No Use')}>
                <CardContent>
                  <Typography color="white" gutterBottom variant="body2">
                    No Use
                  </Typography>
                  <Typography variant="h4" color="white">
                    {stats.noUse}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>

          {/* Quick Actions */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Quick Actions
              </Typography>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<LaptopIcon />}
                    onClick={() => navigate('/laptops')}
                  >
                    Manage Laptops
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<BuildIcon />}
                    onClick={() => navigate('/repairs')}
                  >
                    Repair Records
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<HistoryIcon />}
                    onClick={() => navigate('/history')}
                  >
                    Change History
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<ReportIcon />}
                    onClick={() => navigate('/reports')}
                  >
                    Reports
                  </Button>
                </Grid>
                {(admin?.role === 'admin' || admin?.role === 'super-admin') && (
                  <Grid item xs={12} sm={6} md={3}>
                    <Button
                      variant="outlined"
                      fullWidth
                      startIcon={<PeopleIcon />}
                      onClick={() => navigate('/users')}
                    >
                      User Management
                    </Button>
                  </Grid>
                )}
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    variant="contained"
                    color="secondary"
                    fullWidth
                    startIcon={<SecurityIcon />}
                    onClick={() => navigate('/access-management')}
                  >
                    Access Management
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    variant="contained"
                    fullWidth
                    startIcon={<QrIcon />}
                    onClick={() => navigate('/scan')}
                  >
                    Scan QR Code
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Repair Records - Full Width */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Repair Records
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Active Repairs: <strong>{repairStats.activeRepairs}</strong>
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Cost: <strong>${repairStats.totalCost.toFixed(2)}</strong>
                    </Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<BuildIcon />}
                    onClick={() => navigate('/repairs')}
                  >
                    View All
                  </Button>
                </Box>
              </Box>
              <Divider sx={{ mb: 2 }} />

              {loadingRepairs ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <Typography color="text.secondary">Loading repair records...</Typography>
                </Box>
              ) : repairRecords.length === 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <Typography color="text.secondary">No repair records found</Typography>
                </Box>
              ) : (
                <>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Serial Number</strong></TableCell>
                          <TableCell><strong>Issue Description</strong></TableCell>
                          <TableCell><strong>Service Provider</strong></TableCell>
                          <TableCell><strong>Type</strong></TableCell>
                          <TableCell><strong>Status</strong></TableCell>
                          <TableCell><strong>Sent Date</strong></TableCell>
                          <TableCell><strong>Expected Return</strong></TableCell>
                          <TableCell><strong>Actual Return</strong></TableCell>
                          <TableCell><strong>Cost</strong></TableCell>
                          <TableCell><strong>Invoice</strong></TableCell>
                          <TableCell align="center"><strong>Bill</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {repairRecords.map((repair) => (
                          <TableRow key={repair._id} hover>
                            <TableCell>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 'bold', color: 'primary.main', cursor: 'pointer' }}
                                onClick={() => {
                                  const laptopId = typeof repair.laptopId === 'object' ? repair.laptopId._id : repair.laptopId;
                                  navigate(`/laptops/${laptopId}`);
                                }}
                              >
                                {repair.serialNumber}
                              </Typography>
                            </TableCell>                            <TableCell>
                              <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                                {repair.issueDescription?.substring(0, 50)}{repair.issueDescription?.length > 50 ? '...' : ''}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {repair.serviceProvider || 'N/A'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip label={repair.repairType} size="small" />
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={getRepairStatusLabel(repair.status)}
                                color={getRepairStatusColor(repair.status)}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {formatDate(repair.sentDate)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {formatDate(repair.expectedReturnDate)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color={repair.actualReturnDate ? 'success.main' : 'text.secondary'}>
                                {formatDate(repair.actualReturnDate)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Box>
                                <Typography variant="body2">
                                  {repair.currency === 'INR' ? '₹' : '$'}{repair.cost?.toFixed(2) || '0.00'}
                                </Typography>
                                {repair.laborCost > 0 && (
                                  <Typography variant="caption" color="text.secondary">
                                    Labor: {repair.currency === 'INR' ? '₹' : '$'}{repair.laborCost.toFixed(2)}
                                  </Typography>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                {repair.invoiceNumber || 'N/A'}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              {repair.billUrl ? (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  href={repair.billUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  sx={{ fontSize: '0.7rem', py: 0.5, px: 1 }}
                                >
                                  View
                                </Button>
                              ) : (
                                <Typography variant="caption" color="text.secondary">
                                  No bill
                                </Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* Pagination Controls */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>Records per page</InputLabel>
                        <Select
                          value={repairPageSize}
                          label="Records per page"
                          onChange={handleRepairPageSizeChange}
                        >
                          <MenuItem value={5}>5</MenuItem>
                          <MenuItem value={10}>10</MenuItem>
                          <MenuItem value={25}>25</MenuItem>
                          <MenuItem value={50}>50</MenuItem>
                        </Select>
                      </FormControl>
                      <Typography variant="body2" color="text.secondary">
                        Showing {repairRecords.length === 0 ? 0 : ((repairPage - 1) * repairPageSize) + 1} - {Math.min(repairPage * repairPageSize, totalRepairs)} of {totalRepairs}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Button
                        size="small"
                        onClick={() => handleRepairPageChange(repairPage - 1)}
                        disabled={repairPage === 1}
                        startIcon={<NavigateBefore />}
                      >
                        Previous
                      </Button>
                      <Typography variant="body2">
                        Page {repairPage} of {totalRepairPages}
                      </Typography>
                      <Button
                        size="small"
                        onClick={() => handleRepairPageChange(repairPage + 1)}
                        disabled={repairPage >= totalRepairPages}
                        endIcon={<NavigateNext />}
                      >
                        Next
                      </Button>
                    </Box>
                  </Box>
                </>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Laptop Display Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
        slotProps={{
          paper: { sx: { minHeight: '70vh', maxHeight: '90vh' } }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">{selectedStatus} - {totalLaptops} Laptop(s)</Typography>
            <IconButton onClick={handleCloseDialog} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <Divider />
        <Box sx={{ px: 3, py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'grey.50' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Records per page</InputLabel>
              <Select
                value={pageSize}
                label="Records per page"
                onChange={handlePageSizeChange}
              >
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
                <MenuItem value={150}>150</MenuItem>
                <MenuItem value={200}>200</MenuItem>
              </Select>
            </FormControl>
            <Typography variant="body2" color="text.secondary">
              Showing {filteredLaptops.length === 0 ? 0 : ((page - 1) * pageSize) + 1} - {Math.min(page * pageSize, totalLaptops)} of {totalLaptops}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              size="small"
              onClick={() => handlePageChange(page - 1)}
              disabled={page === 1}
              startIcon={<NavigateBefore />}
            >
              Previous
            </Button>
            <Typography variant="body2">
              Page {page} of {totalPages}
            </Typography>
            <Button
              size="small"
              onClick={() => handlePageChange(page + 1)}
              disabled={page >= totalPages}
              endIcon={<NavigateNext />}
            >
              Next
            </Button>
          </Box>
        </Box>
        <Divider />
        <DialogContent>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
              <Typography>Loading laptops...</Typography>
            </Box>
          ) : filteredLaptops.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
              <Typography color="text.secondary">No laptops found in this category</Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Serial Number</strong></TableCell>
                    <TableCell><strong>Device Name</strong></TableCell>
                    <TableCell><strong>Manufacturer</strong></TableCell>
                    <TableCell><strong>Model</strong></TableCell>
                    <TableCell><strong>Assigned To</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell><strong>Location</strong></TableCell>
                    <TableCell align="center"><strong>Action</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredLaptops.map((laptop) => (
                    <TableRow
                      key={laptop._id}
                      hover
                      sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                    >
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                          {laptop.serialNumber}
                        </Typography>
                      </TableCell>
                      <TableCell>{laptop.deviceName}</TableCell>
                      <TableCell>{laptop.manufacturer}</TableCell>
                      <TableCell>{laptop.model}</TableCell>
                      <TableCell>
                        {laptop.primaryUserDisplayName ? (
                          <Box>
                            <Typography variant="body2">{laptop.primaryUserDisplayName}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {laptop.primaryUserEmail}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">Not Assigned</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getStatusLabel(laptop.status)}
                          color={getStatusColor(laptop.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{laptop.location || 'N/A'}</TableCell>
                      <TableCell align="center">
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleLaptopClick(laptop._id)}
                        >
                          View Details
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Close</Button>
          <Button variant="contained" onClick={() => navigate('/laptops')}>
            View All Laptops
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Dashboard;
